import { useState, useEffect } from "react";
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Tabs,
  Tab,
  Typography,
} from "@mui/material";
import { Add, Edit, Delete } from "@mui/icons-material";
import { useRecordContext, useNotify, useRefresh } from "react-admin";
import { apiUrl } from "../../../httpClient";
import { handleApiError } from "../../../utils/errorHandler";
import { ADMIN_BASE } from "../../../config";
import type {
  ConfigurationResponseDto,
  CreateConfigurationDto,
  UpdateConfigurationDto,
  Environment,
} from "../../../types/api";

interface ConfigDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: CreateConfigurationDto | UpdateConfigurationDto) => Promise<void>;
  editing?: ConfigurationResponseDto;
  environment: Environment;
}

const ConfigDialog = ({
  open,
  onClose,
  onSave,
  editing,
  environment,
}: ConfigDialogProps) => {
  const [key, setKey] = useState(editing?.key || "");
  const [value, setValue] = useState(editing?.value || "");
  const [env, setEnv] = useState<Environment>(editing?.environment || environment);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (editing) {
      setKey(editing.key);
      setValue(editing.value);
      setEnv(editing.environment);
    } else {
      setKey("");
      setValue("");
      setEnv(environment);
    }
  }, [editing, environment]);

  const handleSave = async () => {
    setLoading(true);
    try {
      if (editing) {
        await onSave({ value, environment: env } as UpdateConfigurationDto);
      } else {
        await onSave({ key, value, environment: env } as CreateConfigurationDto);
      }
      onClose();
    } catch (error) {
      // Error handled by parent
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {editing ? "Edit Configuration" : "Add Configuration"}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>
          {!editing && (
            <TextField
              label="Key"
              value={key}
              onChange={(e) => setKey(e.target.value)}
              fullWidth
              required
            />
          )}
          <TextField
            label="Value"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            fullWidth
            required
            multiline
            rows={3}
          />
          <FormControl fullWidth>
            <InputLabel>Environment</InputLabel>
            <Select
              value={env}
              onChange={(e) => setEnv(e.target.value as Environment)}
              label="Environment"
            >
              <MenuItem value="development">Development</MenuItem>
              <MenuItem value="staging">Staging</MenuItem>
              <MenuItem value="production">Production</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={loading}>
          {loading ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const ProviderConfigurations = () => {
  const record = useRecordContext();
  const notify = useNotify();
  const refresh = useRefresh();
  const [configurations, setConfigurations] = useState<
    ConfigurationResponseDto[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<ConfigurationResponseDto | undefined>();
  const [selectedEnv, setSelectedEnv] = useState<Environment>("production");

  useEffect(() => {
    if (record?.id) {
      loadConfigurations();
    }
  }, [record?.id, selectedEnv]);

  const loadConfigurations = async () => {
    if (!record?.id) return;

    setLoading(true);
    try {
      const response = await fetch(
        apiUrl(
          `${ADMIN_BASE}/providers/${record.id}/config${
            selectedEnv ? `?environment=${selectedEnv}` : ""
          }`,
        ),
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("tn_admin_access")}`,
          },
        },
      );

      if (response.ok) {
        const data = await response.json();
        const configs = Array.isArray(data)
          ? data
          : data.configurations || [];
        setConfigurations(configs);
      }
    } catch (error) {
      const appError = handleApiError(error);
      notify(appError.message, { type: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditing(undefined);
    setDialogOpen(true);
  };

  const handleEdit = (config: ConfigurationResponseDto) => {
    setEditing(config);
    setDialogOpen(true);
  };

  const handleDelete = async (key: string, environment: Environment) => {
    if (!record?.id) return;
    if (!confirm(`Delete configuration "${key}"?`)) return;

    try {
      const response = await fetch(
        apiUrl(
          `${ADMIN_BASE}/providers/${record.id}/config/${key}?environment=${environment}`,
        ),
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("tn_admin_access")}`,
          },
        },
      );

      if (response.ok) {
        notify("Configuration deleted successfully", { type: "success" });
        loadConfigurations();
      } else {
        throw new Error("Failed to delete configuration");
      }
    } catch (error) {
      const appError = handleApiError(error);
      notify(appError.message, { type: "error" });
    }
  };

  const handleSave = async (
    data: CreateConfigurationDto | UpdateConfigurationDto,
  ) => {
    if (!record?.id) return;

    try {
      if (editing) {
        const response = await fetch(
          apiUrl(
            `${ADMIN_BASE}/providers/${record.id}/config/${editing.key}`,
          ),
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("tn_admin_access")}`,
            },
            body: JSON.stringify(data),
          },
        );

        if (!response.ok) {
          throw new Error("Failed to update configuration");
        }
        notify("Configuration updated successfully", { type: "success" });
      } else {
        const response = await fetch(
          apiUrl(`${ADMIN_BASE}/providers/${record.id}/config`),
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("tn_admin_access")}`,
            },
            body: JSON.stringify(data),
          },
        );

        if (!response.ok) {
          throw new Error("Failed to create configuration");
        }
        notify("Configuration added successfully", { type: "success" });
      }
      loadConfigurations();
    } catch (error) {
      const appError = handleApiError(error);
      notify(appError.message, { type: "error" });
      throw error;
    }
  };

  if (!record) return null;

  const filteredConfigs = configurations.filter(
    (config) => config.environment === selectedEnv,
  );

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
        <Tabs
          value={selectedEnv}
          onChange={(_, newValue) => setSelectedEnv(newValue)}
        >
          <Tab label="Development" value="development" />
          <Tab label="Staging" value="staging" />
          <Tab label="Production" value="production" />
        </Tabs>
        <Button
          startIcon={<Add />}
          onClick={handleAdd}
          variant="contained"
          size="small"
        >
          Add Configuration
        </Button>
      </Box>

      {loading ? (
        <Typography>Loading configurations...</Typography>
      ) : filteredConfigs.length === 0 ? (
        <Typography color="text.secondary">
          No configurations found for {selectedEnv} environment
        </Typography>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Key</TableCell>
                <TableCell>Value</TableCell>
                <TableCell>Environment</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredConfigs.map((config) => (
                <TableRow key={`${config.key}-${config.environment}`}>
                  <TableCell>{config.key}</TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{
                        maxWidth: 400,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {config.value}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={config.environment} size="small" />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => handleEdit(config)}
                    >
                      <Edit fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(config.key, config.environment)}
                      color="error"
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <ConfigDialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setEditing(undefined);
        }}
        onSave={handleSave}
        editing={editing}
        environment={selectedEnv}
      />
    </Box>
  );
};
